import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, TRUST_BAR, WHY_US, TESTIMONIALS, HIGHLIGHTS, STORE_STATS } from "../mock/mock";
import { useProducts } from "../context/ProductsContext";
import { useContent } from "../context/ContentContext";
import HeroCarousel from "../components/HeroCarousel";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Award, BadgeCheck, BadgeIndianRupee, Gem, Gift, Leaf, Package, ShieldCheck, Sparkles, Sprout, Star, Truck, Quote } from "lucide-react";
import { Button } from "../components/ui/button";

const iconMap = { Sprout, ShieldCheck, Package, BadgeCheck, Truck, Gift, Award, Leaf, Gem, BadgeIndianRupee, Sparkles };

const SectionTitle = ({ eyebrow, title, subtitle, center = true }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl mb-8 md:mb-10 px-4`}>
    {eyebrow && <div className="text-[11px] tracking-[0.28em] font-bold text-olive mb-2">{eyebrow}</div>}
    <h2 className="font-serif-brand text-[28px] md:text-[38px] font-bold text-[#2a1a15] leading-tight">{title}</h2>
    {subtitle && <p className="mt-2.5 text-[14.5px] md:text-[16px] text-[#6b4f39]">{subtitle}</p>}
  </div>
);

const HomePage = () => {
  const { products: ALL } = useProducts();
  const { content } = useContent();
  const promoCards = (content.promo_cards || []).filter((p) => p.active !== false);
  const bestsellers = ALL.filter((p) => p.tags?.includes("BESTSELLER") || p.tags?.includes("PREMIUM")).slice(0, 8);
  const snacking = ALL.filter((p) => p.tags?.includes("SNACK") || p.category === "breakfast").slice(0, 4);
  const combos = ALL.filter((p) => p.tags?.includes("BESTSELLER")).slice(0, 4);
  const seeds = ALL.filter((p) => p.category === "seeds" || p.category === "berries").slice(0, 4);

  return (
    <main className="bg-cream">
      <HeroCarousel />

      {/* Highlights strip */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {HIGHLIGHTS.map((h) => {
            const I = iconMap[h.icon] || Leaf;
            return (
              <div key={h.title} className="flex items-center gap-3 bg-white rounded-xl border border-[#efe4c9] p-3.5 md:p-4">
                <div className="h-11 w-11 rounded-full bg-[#f9f1de] flex items-center justify-center"><I className="h-5 w-5 text-brand" /></div>
                <div className="font-semibold text-[13px] md:text-[14px] text-[#2a1a15]">{h.title}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Shop by Category */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <SectionTitle eyebrow="OUR RANGE" title="Shop by Category" subtitle="A wide range of premium quality dry fruits, nuts, seeds & more." />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {CATEGORIES.map((c) => (
            <Link key={c.id} to={`/shop?cat=${c.id}`} className="ardf-tile relative rounded-2xl overflow-hidden aspect-[3/4] group">
              <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${c.tint} 0%, ${c.tint}cc 100%)` }} />
              <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-90 group-hover:opacity-100" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3.5 md:p-4 text-white">
                <div className="font-serif-brand text-[18px] md:text-[20px] font-bold">{c.name}</div>
                <div className="text-[11px] tracking-[0.15em] opacity-80">SHOP NOW →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Top Selling */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <div className="flex items-end justify-between mb-6 md:mb-8 px-1">
          <div>
            <div className="text-[11px] tracking-[0.28em] font-bold text-olive mb-1.5">CUSTOMER FAVOURITES</div>
            <h2 className="font-serif-brand text-[28px] md:text-[38px] font-bold text-[#2a1a15] leading-tight">Top Selling Products</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-1.5 text-brand font-semibold text-[14px] hover:gap-2.5 transition-all">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {bestsellers.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Nutty Delight Offers */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <SectionTitle eyebrow="LIMITED TIME" title="Festive Nutty Offers" subtitle="Save more when you stock up on your daily nutrition essentials." />
        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {promoCards.length === 0 ? (
            <div className="md:col-span-3 text-center text-[#6b4f39] text-sm py-6">No active offers right now — check back soon.</div>
          ) : promoCards.map((c) => (
            <Link key={c.id} to={c.to || "/shop"} className="ardf-tile relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[3/4] group" style={{ backgroundColor: c.tint || "#4a2018" }}>
              <img src={c.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/30 to-transparent" style={{ background: `linear-gradient(135deg, ${c.tint || "#4a2018"}dd, ${c.tint || "#4a2018"}66, transparent)` }} />
              <div className="relative p-6 md:p-8 h-full flex flex-col justify-between text-white">
                <div>
                  {c.eyebrow && <div className="text-gold text-[11px] tracking-[0.28em] font-bold mb-2">{c.eyebrow}</div>}
                  <div className="font-serif-brand text-3xl md:text-4xl font-bold leading-tight">{c.title}</div>
                  {c.code && <div className="text-sm mt-2 text-[#f2e7d1]">Use code: <span className="text-gold font-bold">{c.code}</span></div>}
                </div>
                <span className="inline-flex w-fit items-center gap-1.5 bg-gold text-[#2a1a15] px-4 py-2 rounded-full font-semibold text-sm">Shop Now <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Healthy Snacking */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <div className="text-[11px] tracking-[0.28em] font-bold text-olive mb-1.5">EAT WELL, LIVE WELL</div>
            <h2 className="font-serif-brand text-[28px] md:text-[38px] font-bold text-[#2a1a15]">Healthy Snacking</h2>
          </div>
          <Link to="/shop?cat=snacks" className="hidden md:inline-flex items-center gap-1.5 text-brand font-semibold text-[14px] hover:gap-2.5 transition-all">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {snacking.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Trust bar */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 bg-[#2a1a15] rounded-2xl overflow-hidden divide-x divide-[#4a2d24]">
          {TRUST_BAR.map((t, i) => {
            const I = iconMap[t.icon] || Truck;
            return (
              <div key={i} className="flex items-center justify-center gap-3 p-5 md:p-7 text-white">
                <I className="h-7 w-7 text-gold" />
                <div>
                  <div className="font-semibold text-[14px] md:text-[15px]">{t.title}</div>
                  <div className="text-[12px] text-[#e5d3ac]">{t.subtitle}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Seeds & Berries */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <div className="text-[11px] tracking-[0.28em] font-bold text-olive mb-1.5">SUPERFOODS</div>
            <h2 className="font-serif-brand text-[28px] md:text-[38px] font-bold text-[#2a1a15]">Seeds, Berries & Wellness</h2>
          </div>
          <Link to="/shop?cat=seeds" className="hidden md:inline-flex items-center gap-1.5 text-brand font-semibold text-[14px] hover:gap-2.5 transition-all">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {seeds.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Legacy story */}
      <section className="mt-16 md:mt-24 bg-[#f3ecda]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative">
            <div className="absolute -top-4 -left-4 h-28 w-28 rounded-full bg-gold/30 blur-2xl" />
            <img src="https://images.pexels.com/photos/5254831/pexels-photo-5254831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Legacy" className="relative rounded-3xl w-full h-[420px] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -right-4 md:-right-6 bg-white rounded-2xl shadow-lg p-4 md:p-5 border border-[#efe4c9]">
              <div className="font-serif-brand text-3xl md:text-4xl font-bold text-brand">50+</div>
              <div className="text-[12px] tracking-[0.15em] text-olive font-semibold">YEARS OF TRUST</div>
            </div>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.28em] font-bold text-olive mb-2">OUR STORY</div>
            <h2 className="font-serif-brand text-[32px] md:text-[44px] font-bold text-[#2a1a15] leading-[1.1]">From 1973 — A Legacy of Goodness</h2>
            <p className="mt-4 text-[15px] md:text-[16px] text-[#5c4132] leading-relaxed">From a humble beginning in 1973 to becoming a trusted name in premium dry fruits, our journey is built on quality, trust and tradition. Today, under the leadership of Nitin Agrawal, we continue our commitment to excellence — sourcing carefully, packing hygienically, and delivering goodness that stays.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {STORE_STATS.map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-[#efe4c9] p-4">
                  <div className="font-serif-brand text-2xl md:text-3xl font-bold text-brand">{s.count}</div>
                  <div className="text-[12.5px] text-[#6b4f39]">{s.label}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="mt-7 inline-flex items-center gap-2 bg-brand hover:bg-[#6c1414] text-white font-semibold px-6 py-3 rounded-full transition-colors">Read Our Story <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <SectionTitle eyebrow="WHY AGRAWAL RICH" title="Why Choose Us" subtitle="We deliver more than just dry fruits — we deliver trust, quality & a promise of goodness." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {WHY_US.map((w) => {
            const I = iconMap[w.icon] || Leaf;
            return (
              <div key={w.title} className="bg-white rounded-2xl border border-[#efe4c9] p-5 md:p-6 hover:border-brand/50 hover:shadow-md transition-all">
                <div className="h-12 w-12 rounded-full bg-[#f9f1de] flex items-center justify-center mb-4"><I className="h-5.5 w-5.5 text-brand" /></div>
                <h3 className="font-serif-brand text-[18px] font-bold text-[#2a1a15]">{w.title}</h3>
                <p className="mt-1.5 text-[13.5px] text-[#6b4f39] leading-relaxed">{w.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Combos banner */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <div className="relative rounded-3xl overflow-hidden bg-[#2a1a15]">
          <img src="https://images.pexels.com/photos/17878238/pexels-photo-17878238.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="relative grid md:grid-cols-2 gap-6 p-8 md:p-14 text-white">
            <div>
              <div className="text-gold text-[11px] tracking-[0.28em] font-bold mb-2">GIFTING MADE ELEGANT</div>
              <h2 className="font-serif-brand text-[32px] md:text-[44px] font-bold leading-tight">Healthy Family<br />Dry Fruits Combo</h2>
              <p className="mt-3 text-[#f2e7d1] max-w-md text-[15px]">Get more nutrition at great value — a cost-effective way to stock up on daily essentials while ensuring quality, taste, and health benefits for the entire family.</p>
              <Link to="/shop?cat=combos" className="mt-6 inline-flex items-center gap-2 bg-gold hover:bg-[#b6944f] text-[#2a1a15] font-semibold px-6 py-3 rounded-full transition-colors">Shop Combos <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {combos.slice(0, 4).map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="bg-white/95 rounded-xl overflow-hidden hover:scale-[1.02] transition-transform">
                  <img src={p.image} alt={p.name} className="h-24 md:h-32 w-full object-cover" />
                  <div className="p-2.5">
                    <div className="text-[12.5px] font-semibold text-[#2a1a15] line-clamp-1">{p.name}</div>
                    <div className="text-[13px] font-bold text-brand mt-0.5">₹{p.price.toLocaleString("en-IN")}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-14 md:mt-20">
        <SectionTitle eyebrow="HAPPY FAMILIES" title="Loved Across India" subtitle="Real stories from families who have trusted us for generations." />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#efe4c9] p-5 md:p-6 relative">
              <Quote className="h-8 w-8 text-gold/70 mb-2" />
              <p className="text-[14.5px] text-[#3a2a20] leading-relaxed">“{t.text}”</p>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[#2a1a15] text-[14px]">{t.name}</div>
                  <div className="text-[12px] text-[#6b4f39]">{t.role}</div>
                </div>
                <div className="flex text-gold">{Array.from({ length: t.rating }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 fill-current" />)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Store finder */}
      <section className="mt-14 md:mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 grid md:grid-cols-2 gap-8 items-center">
          <div className="relative rounded-3xl overflow-hidden">
            <img src="https://images.unsplash.com/photo-1704738986112-96bf5e9d8b60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxkcnklMjBmcnVpdHMlMjBzaG9wfGVufDB8fHx8MTc4MjkxNDU3Mnww&ixlib=rb-4.1.0&q=85" alt="Store" className="w-full h-[380px] md:h-[440px] object-cover" />
            <div className="absolute top-6 left-6 bg-white/95 rounded-xl px-4 py-2 shadow-md">
              <div className="font-serif-brand text-brand text-lg font-bold">Agrawal Rich</div>
              <div className="text-[10px] tracking-[0.2em] text-olive">SINCE 1973 · BANGALORE</div>
            </div>
          </div>
          <div>
            <h2 className="font-serif-brand text-[32px] md:text-[42px] font-bold text-[#2a1a15] leading-tight">Hey, We're Right Around the Corner!</h2>
            <p className="mt-3 text-[15px] text-[#5c4132]">Visit our flagship store in Bangalore or shop online across India. Get your daily nutrition sorted with 50+ years of trust behind every pack.</p>
            <div className="mt-6 flex gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-[#6c1414] text-white font-semibold px-6 py-3 rounded-full transition-colors">Find Us <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/shop" className="inline-flex items-center gap-2 border border-[#3a2a20] text-[#2a1a15] hover:bg-[#2a1a15] hover:text-white font-medium px-6 py-3 rounded-full transition-colors">Shop Online</Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {STORE_STATS.slice(0, 3).map((s) => (
                <div key={s.label} className="text-center bg-white rounded-xl border border-[#efe4c9] py-4">
                  <div className="font-serif-brand text-2xl md:text-3xl font-bold text-brand">{s.count}</div>
                  <div className="text-[11.5px] text-[#6b4f39] px-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="h-4" />
    </main>
  );
};

export default HomePage;
